import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {SelectedElementType} from 'components/Accommodation/ReviewsSection'
import {orderedAvailableRatings} from 'components/Accommodation/ReviewsSection/utils'
import {RatingType} from 'types/Hotel'
import {reviewsLabels} from 'utils/reviewsMessages'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Tag} from '@vio/atlas/Tag'
import {Text} from '@vio/atlas/Text'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/hooks/useDispatchTeamEvent'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action} from '@vio/core/src/analytics/types/Events'
import {Category, Entity} from '@vio/core/src/analytics/types/Events'

const getRoundedRating = (rating: number) => {
  return Math.round(rating * 10) / 10
}

interface Props {
  guestRating: RatingType
}

const SummaryIcons: Record<string, React.ReactElement> = {
  location: <Icon name="Pin" />,
  facilities: <Icon name="Dumbell" />,
  service: <Icon name="ServiceBell" />,
  dining: <Icon name="Restaurant" />,
  cleanliness: <Icon name="Clean" />,
  rooms: <Icon name="Room" />
}

export const ReviewSummary: React.FC<Props> = ({guestRating}) => {
  const dispatchTeamEvent = useDispatchTeamEvent()
  const {formatMessage} = useIntl()

  const ratingsToDisplay = useMemo(
    () => orderedAvailableRatings(Object.keys(guestRating)),
    [guestRating]
  )

  const handleClick = (type: SelectedElementType) => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.HotelDetailsReviewsSection,
        action: Action.Clicked,
        payload: {
          selectedElement: type
        }
      })
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      gap="s300"
      flexWrap="wrap"
      justifyContent="center"
      desktopSm={{
        justifyContent: 'flex-start'
      }}
    >
      {ratingsToDisplay.map(rating => (
        <div
          key={rating}
          onClick={() => handleClick(SelectedElementType.RatingElement)}
          role="presentation"
        >
          <Tag
            iconStart={SummaryIcons[rating]}
            iconEnd={
              <Text variant="labelS">
                {getRoundedRating(guestRating[rating])}
              </Text>
            }
            variant="neutral"
            size="md"
          >
            <Text variant="labelS">{formatMessage(reviewsLabels[rating])}</Text>
          </Tag>
        </div>
      ))}
    </Box>
  )
}
